import { useState } from "react";
import { useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchPlaces, placeKey, type PlaceFilter } from "../../lib/dashboard-data";
import {
  finRangeDays, finRangeFromParams, finRangeKey, finRangeQuery, finRangeToParams, type FinRange,
} from "../../lib/finance-report";
import { dmy } from "../../lib/stats-range";

/** state ของตัวกรองจอการเงิน (ช่วงเวลา + พื้นที่ ใน URL) — แยกจาก FinanceFilters.tsx ให้ fast refresh ทำงาน */

const PLACE_KEYS = ["province", "district", "tasaeng"] as const;
const RANGE_KEYS = ["period", "from", "to"] as const;

export function useFinanceFilters() {
  const [params, setParams] = useSearchParams();
  // "ตอนนี้" คิดครั้งเดียวตอนเปิดจอ — ห้ามเรียก Date.now() ระหว่าง render (query key จะเปลี่ยนทุกครั้ง)
  const [nowMs] = useState(() => Date.now());
  const range = finRangeFromParams(params);
  const place: PlaceFilter = {};
  for (const k of PLACE_KEYS) {
    const v = params.get(k);
    if (v && /^[0-9a-f-]{36}$/i.test(v)) place[k] = v;
  }

  function go(next: { range?: FinRange; place?: PlaceFilter; extra?: Record<string, string | null> }) {
    const p = new URLSearchParams(params);
    if (next.range) {
      for (const k of RANGE_KEYS) p.delete(k);
      for (const [k, v] of Object.entries(finRangeToParams(next.range))) p.set(k, v);
    }
    if (next.place) {
      for (const k of PLACE_KEYS) p.delete(k);
      for (const k of PLACE_KEYS) {
        const v = next.place[k];
        if (v) p.set(k, v);
      }
    }
    for (const [k, v] of Object.entries(next.extra ?? {})) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  }

  const days = finRangeDays(range, nowMs);
  return {
    params,
    nowMs,
    range,
    place,
    days,
    /** query ของ API — ช่วง + ตัวกรองพื้นที่ (ระดับที่ลึกสุดคือขอบเขตจริง) */
    query: {
      ...finRangeQuery(range, nowMs),
      ...(place.province ? { province_id: place.province } : {}),
      ...(place.district ? { district_id: place.district } : {}),
      ...(place.tasaeng ? { tasaeng_id: place.tasaeng } : {}),
    },
    key: `${finRangeKey(range)}|${placeKey(place)}`,
    periodText: `${dmy(days.from)} – ${dmy(days.to)}`,
    go,
  };
}

export type FinanceFiltersState = ReturnType<typeof useFinanceFilters>;

/** ชื่อพื้นที่ที่กำลังดู (ระดับที่ลึกสุด) — ใช้ในหัวจอและชื่อไฟล์ */
export function usePlaceName(place: PlaceFilter): string {
  const places = useQuery({ queryKey: ["stats", "places"], queryFn: fetchPlaces, staleTime: 5 * 60_000 });
  const d = places.data;
  if (!d) return "…";
  if (place.tasaeng) return d.tasaengs.find((t) => t.id === place.tasaeng)?.name_lo ?? "…";
  if (place.district) return d.districts.find((x) => x.id === place.district)?.name_lo ?? "…";
  if (place.province) return d.districts.find((x) => x.parent_id === place.province)?.parent_name_lo ?? "…";
  return "ທຸກພື້ນທີ່ໃນຂອບເຂດ";
}

